// src/stores/memoryStore.ts

import { create } from 'zustand';
import { supabase } from '../lib/supabase';

export interface Memory {
  id: string;
  key: string;
  memory_text: string;
  metadata: Record<string, any> | null;
  memory_type: 'preference' | 'pattern' | 'context' | 'milestone' | 'insight';
  category: 'food' | 'behavior' | 'goals' | 'lifestyle' | 'emotional' | 'schedule' | 'health' | null;
  tags: string[] | null;
  confidence: number;
  source: 'chat' | 'behavior' | 'explicit' | 'pattern_detection';
  created_at: string;
  last_updated_at: string;
  last_referenced_at: string | null;
  is_active: boolean;
}

interface MemoryStore {
  // State
  memories: Memory[];
  isLoading: boolean;
  error: string | null;
  realtimeSubscription: any | null;

  // Actions
  loadMemories: () => Promise<void>;
  subscribeToMemories: (userId: string) => void;
  unsubscribeFromMemories: () => void;
  addMemory: (memory: Memory) => void;
  updateMemory: (key: string, updates: Partial<Memory>) => void;
  deleteMemory: (key: string) => void;
  getActiveMemories: (limit?: number) => Memory[];
  searchMemories: (query: string) => Memory[];
  clearMemories: () => void;
}

export const useMemoryStore = create<MemoryStore>((set, get) => ({
  // --------------------
  // State
  // --------------------
  memories: [],
  isLoading: false,
  error: null,
  realtimeSubscription: null,

  // --------------------
  // Load memories on init
  // --------------------
  loadMemories: async () => {
    set({ isLoading: true, error: null });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('user_memory')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('confidence', { ascending: false })
        .order('last_updated_at', { ascending: false });

      if (error) throw error;

      set({ memories: data || [], isLoading: false });
    } catch (err: any) {
      console.error('[memoryStore] Load failed:', err);
      set({ error: err.message, isLoading: false });
    }
  },

  // --------------------
  // Subscribe to realtime updates
  // --------------------
  subscribeToMemories: (userId: string) => {
    const { realtimeSubscription } = get();

    // Prevent duplicate subscriptions
    if (realtimeSubscription) {
      console.warn('[memoryStore] Already subscribed to realtime updates');
      return;
    }

    const subscription = supabase
      .channel(`user_memory:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_memory',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          // console.log('[memoryStore] New memory:', payload.new);
          get().addMemory(payload.new as Memory);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_memory',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          // console.log('[memoryStore] Updated memory:', payload.new);
          const updated = payload.new as Memory;
          
          // If marked inactive, remove from store
          if (!updated.is_active) {
            get().deleteMemory(updated.key);
          } else {
            get().updateMemory(updated.key, updated);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'user_memory',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          // console.log('[memoryStore] Deleted memory:', payload.old);
          const deleted = payload.old as Memory;
          get().deleteMemory(deleted.key);
        }
      )
      .subscribe();

    set({ realtimeSubscription: subscription });
    // console.log('[memoryStore] Subscribed to realtime updates');
  },

  // --------------------
  // Unsubscribe from realtime
  // --------------------
  unsubscribeFromMemories: () => {
    const { realtimeSubscription } = get();

    if (realtimeSubscription) {
      supabase.removeChannel(realtimeSubscription);
      set({ realtimeSubscription: null });
      // console.log('[memoryStore] Unsubscribed from realtime updates');
    }
  },

  // --------------------
  // Local state updates (triggered by realtime)
  // --------------------
  addMemory: (memory: Memory) => {
    set((state) => {
      // Prevent duplicates
      const exists = state.memories.some((m) => m.id === memory.id);
      if (exists) return state;

      return {
        memories: [memory, ...state.memories].sort((a, b) => {
          // Sort by confidence DESC, then last_updated_at DESC
          if (b.confidence !== a.confidence) {
            return b.confidence - a.confidence;
          }
          return new Date(b.last_updated_at).getTime() - new Date(a.last_updated_at).getTime();
        }),
      };
    });
  },

  updateMemory: (key: string, updates: Partial<Memory>) => {
    set((state) => ({
      memories: state.memories.map((m) =>
        m.key === key ? { ...m, ...updates } : m
      ),
    }));
  },

  deleteMemory: (key: string) => {
    set((state) => ({
      memories: state.memories.filter((m) => m.key !== key),
    }));
  },

  // --------------------
  // Get top memories for context
  // --------------------
  getActiveMemories: (limit = 15) => {
    return get().memories.slice(0, limit);
  },

  // --------------------
  // Search memories (simple text match)
  // --------------------
  searchMemories: (query: string) => {
    const lowerQuery = query.toLowerCase();
    return get().memories.filter((m) =>
      m.memory_text.toLowerCase().includes(lowerQuery) ||
      m.key.toLowerCase().includes(lowerQuery) ||
      m.tags?.some((tag) => tag.toLowerCase().includes(lowerQuery))
    );
  },

  // --------------------
  // Clear all memories (logout)
  // --------------------
  clearMemories: () => {
    get().unsubscribeFromMemories();
    set({ 
      memories: [], 
      isLoading: false, 
      error: null,
      realtimeSubscription: null
    });
  },
}));